import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import * as Yup from "yup";
import axiosInstance from "../../../utils/axiosInstance";
import { modalStyle } from "../../../utils/styles";

const CONTENT_TYPES = [
  { value: "article", label: "Article" },
  { value: "pdf", label: "PDF" },
  { value: "video", label: "Video" },
  { value: "infographic", label: "Infographic" },
];

// Tabs for Uploading via File or URL
const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tab-panel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ mt: 2 }}>{children}</Box>}
    </div>
  );
};

const ContentModal = ({ open, onClose, onSubmit, content }) => {
  const [tabIndex, setTabIndex] = useState(0); // To manage upload or URL tab
  const [coverPreview, setCoverPreview] = useState(null); // Preview for uploaded file
  const [categories, setCategories] = useState(null);

  const formik = useFormik({
    initialValues: {
      title: content?.title || "",
      description: content?.description || "",
      category_id: content?.category_id || -1,
      type: content?.type || -1,
      coverImageUrl: "",
      fileUrl: "",
      mediaUrl: "",
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Title is required"),
      description: Yup.string().required("Description is required"),
      category_id: Yup.string()
        .notOneOf([-1], "Category is required")
        .required("Category is required"),
      type: Yup.string().required("Type is required"),
      mediaUrl: Yup.string().url("Must be a valid URL"),
      coverImageUrl: Yup.mixed().required("Cover Image is required"),
      fileUrl: Yup.mixed(),
    }),
    onSubmit: (values) => {
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("description", values.description);
      formData.append("type", values.type);
      formData.append("category_id", values.category_id);
      if (values.coverImageUrl) {
        formData.append("cover_image", values.coverImageUrl);
      }
      if (values.fileUrl) {
        formData.append("file_url", values.fileUrl);
      }
      if (values.mediaUrl) {
        formData.append("media_url", values.mediaUrl);
      }

      onSubmit(values);

      formik.resetForm();
      setCoverPreview(null);
      onClose();
    },
  });

  const handleTabChange = (_event, newValue) => {
    setTabIndex(newValue); // Update tab index for file or URL
  };

  const handleCoverImageChange = (event) => {
    const file = event.currentTarget.files[0];
    formik.setFieldValue("coverImageUrl", file);
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => setCoverPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const getAcceptedFileType = () => {
    switch (formik.values.type) {
      case "video":
        return "video/*"; // Accept all video types
      case "pdf":
        return "application/pdf"; // Accept PDF files only
      case "article":
        return ".md,.markdown"; // Accept markdown files
      case "infographic":
      default:
        return "image/*"; // Accept all image types (jpeg, png, etc.)
    }
  };

  const handleFileChange = (event) => {
    const file = event.currentTarget.files[0];
    formik.setFieldValue("fileUrl", file);
  };

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_URL}/categories`,
      );

      if (response.status === 200) {
        setCategories(response.data);
      }
    } catch (error) {}
  }, []);

  useEffect(() => {
    if (open) {
      fetchCategories();
    }
  }, [open, fetchCategories]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography variant="h6" component="h2">
          {content ? "Edit Content" : "Add New Content"}
        </Typography>

        <form onSubmit={formik.handleSubmit}>
          <TextField
            fullWidth
            margin="normal"
            placeholder="Title"
            name="title"
            variant="outlined"
            value={formik.values.title}
            onChange={formik.handleChange}
            error={formik.touched.title && formik.errors.title}
            helperText={formik.touched.title && formik.errors.title}
          />

          <FormControl fullWidth margin="normal">
            <Select
              id="category_id"
              name="category_id"
              value={formik.values.category_id}
              onChange={formik.handleChange}
              error={formik.touched.category_id && formik.errors.category_id}
            >
              <MenuItem value={-1}>--Select a category--</MenuItem>
              {categories ? (
                categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))
              ) : (
                <Typography sx={{ textAlign: "center", padding: 1 }}>
                  Please add some categories first.
                </Typography>
              )}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <Select
              id="type"
              name="type"
              value={formik.values.type}
              onChange={formik.handleChange}
              error={formik.touched.type && formik.errors.type}
            >
              <MenuItem value={-1}>--Select type--</MenuItem>
              {CONTENT_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {formik.values.type && (
            <>
              <TextField
                fullWidth
                margin="normal"
                placeholder="Description"
                name="description"
                variant="outlined"
                multiline
                minRows={4}
                maxRows={10}
                value={formik.values.description}
                onChange={formik.handleChange}
                error={
                  formik.touched.description &&
                  Boolean(formik.errors.description)
                }
                helperText={
                  formik.touched.description && formik.errors.description
                }
              />

              <InputLabel>Cover Image</InputLabel>
              <Button component="label" variant="contained" sx={{ mt: 2 }}>
                Upload Image
                <input type="file" hidden onChange={handleCoverImageChange} />
              </Button>
              <Typography color="error">
                {formik.errors.coverImageUrl}
              </Typography>
              {coverPreview && (
                <Box
                  component="img"
                  src={coverPreview}
                  alt="Cover Preview"
                  accept="image/*"
                  sx={{ width: "100%", height: "auto", mt: 2 }}
                />
              )}

              {/* Tabs for File or URL */}
              <Tabs value={tabIndex} onChange={handleTabChange} sx={{ mt: 2 }}>
                {formik.values.type === "video" && (
                  <Tab label={`Upload ${formik.values.type}`} />
                )}
                {formik.values.type === "video" && (
                  <Tab label="Use Media URL" />
                )}
              </Tabs>

              {/* File Upload Tab */}
              {(formik.values.type === "pdf" ||
                formik.values.type === "video") && (
                <TabPanel value={tabIndex} index={0}>
                  <Button component="label" variant="contained" sx={{ mt: 2 }}>
                    Upload File
                    <input
                      type="file"
                      accept={getAcceptedFileType()}
                      hidden
                      onChange={handleFileChange}
                    />
                  </Button>
                </TabPanel>
              )}

              {/* Media URL Tab */}
              {formik.values.type === "video" && (
                <TabPanel value={tabIndex} index={1}>
                  <TextField
                    fullWidth
                    margin="normal"
                    placeholder="Media URL"
                    name="mediaUrl"
                    value={formik.values.mediaUrl}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.mediaUrl && Boolean(formik.errors.mediaUrl)
                    }
                    helperText={
                      formik.touched.mediaUrl && formik.errors.mediaUrl
                    }
                  />
                </TabPanel>
              )}
            </>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 3 }}
          >
            {content ? "Update Content" : "Add Content"}
          </Button>
        </form>
      </Box>
    </Modal>
  );
};

export default ContentModal;
